export type StyleVariableType = 'color' | 'font' | 'length';

export type StyleVariableGroup =
  | 'accent'
  | 'accent-secondary'
  | 'highlight'
  | 'danger'
  | 'status'
  | 'text'
  | 'surface'
  | 'page'
  | 'invitation'
  | 'typography'
  | 'layout';

export interface StyleVariableDef {
  /** CSS custom property name, including the leading `--`. The key used on both sides of the API contract. */
  name: string;
  /** Human label for the admin UI. */
  label: string;
  group: StyleVariableGroup;
  type: StyleVariableType;
  /**
   * Resolved value baked into src/styles.scss. Pre-fills the form before
   * the API responds, and fills in for any name the backend doesn't
   * return (see StyleVariablesService.apply()).
   */
  default: string;
  /**
   * Informational only — not used by the API. Some variables are defined
   * in styles.scss as `var(--x)` rather than a literal value, so they
   * inherit another variable's value until explicitly overridden here.
   * Shown in the admin UI as a hint; editing the field stores an
   * independent value like any other variable.
   */
  derivedFrom?: string;
}

/** The full, ordered set of editable style variables. Keep in sync with src/styles.scss :root. */
export const STYLE_VARIABLE_DEFS: readonly StyleVariableDef[] = [
  // ── Primary accent ──────────────────────────────────────────────────────
  { name: '--color-accent', label: 'Accent', group: 'accent', type: 'color', default: '#9e4a1c' },
  { name: '--color-accent-soft', label: 'Accent — soft', group: 'accent', type: 'color', default: '#2a4a2a' },
  { name: '--color-accent-hover', label: 'Accent — hover', group: 'accent', type: 'color', default: '#b8612e' },
  { name: '--color-accent-text', label: 'Accent — text', group: 'accent', type: 'color', default: '#9e4a1c' },
  {
    name: '--color-accent-pressed',
    label: 'Accent — pressed',
    group: 'accent',
    type: 'color',
    default: '#9e4a1c',
    derivedFrom: '--color-accent-text',
  },

  // ── Secondary accent (blush) ────────────────────────────────────────────
  {
    name: '--color-accent-secondary-text',
    label: 'Secondary accent — text',
    group: 'accent-secondary',
    type: 'color',
    default: '#9e4a1c',
  },
  {
    name: '--color-accent-secondary-soft',
    label: 'Secondary accent — soft',
    group: 'accent-secondary',
    type: 'color',
    default: '#3a5a3a',
  },
  {
    name: '--color-accent-secondary-hover',
    label: 'Secondary accent — hover',
    group: 'accent-secondary',
    type: 'color',
    default: '#3a5a3a',
    derivedFrom: '--color-accent-secondary-soft',
  },

  // ── Highlight accent (gold) ─────────────────────────────────────────────
  { name: '--color-highlight-text', label: 'Highlight — text', group: 'highlight', type: 'color', default: '#d4a847' },
  { name: '--color-highlight-soft', label: 'Highlight — soft', group: 'highlight', type: 'color', default: '#e8c76a' },

  // ── Danger ───────────────────────────────────────────────────────────────
  { name: '--color-danger', label: 'Danger', group: 'danger', type: 'color', default: '#c0392b' },
  { name: '--color-danger-soft', label: 'Danger — soft', group: 'danger', type: 'color', default: '#f7e8e8' },
  {
    name: '--color-danger-text',
    label: 'Danger — banner text',
    group: 'danger',
    type: 'color',
    default: '#b8612e',
    derivedFrom: '--color-accent-hover',
  },

  // ── Status pills ─────────────────────────────────────────────────────────
  { name: '--color-status-pending-text', label: 'Status: pending — text', group: 'status', type: 'color', default: '#92400e' },
  { name: '--color-status-pending-soft', label: 'Status: pending — soft', group: 'status', type: 'color', default: '#fef3c7' },
  { name: '--color-status-approved-text', label: 'Status: approved — text', group: 'status', type: 'color', default: '#065f46' },
  { name: '--color-status-approved-soft', label: 'Status: approved — soft', group: 'status', type: 'color', default: '#d1fae5' },
  { name: '--color-status-rejected-text', label: 'Status: rejected — text', group: 'status', type: 'color', default: '#991b1b' },
  { name: '--color-status-rejected-soft', label: 'Status: rejected — soft', group: 'status', type: 'color', default: '#fee2e2' },

  // ── Text ─────────────────────────────────────────────────────────────────
  { name: '--color-text', label: 'Text (dark theme)', group: 'text', type: 'color', default: '#bd7b32' },
  { name: '--color-text-on-surface', label: 'Text on surface', group: 'text', type: 'color', default: '#d4a847' },
  {
    name: '--color-text-on-surface-muted',
    label: 'Text on surface — muted',
    group: 'text',
    type: 'color',
    default: '#c2a55a',
  },

  // ── Neutral surfaces ─────────────────────────────────────────────────────
  { name: '--color-surface', label: 'Surface', group: 'surface', type: 'color', default: '#1e3620' },
  {
    name: '--color-on-accent',
    label: 'Text on accent',
    group: 'surface',
    type: 'color',
    default: '#1e3620',
    derivedFrom: '--color-surface',
  },
  { name: '--color-content-bg', label: 'Content background', group: 'surface', type: 'color', default: '#1a2e1a' },
  {
    name: '--color-toast-text',
    label: 'Toast text',
    group: 'surface',
    type: 'color',
    default: '#1a2e1a',
    derivedFrom: '--color-content-bg',
  },
  {
    name: '--color-toolbar-bg',
    label: 'Toolbar background',
    group: 'surface',
    type: 'color',
    default: '#d4a847',
    derivedFrom: '--color-text-on-surface',
  },

  // ── Page chrome ──────────────────────────────────────────────────────────
  { name: '--color-page-bg', label: 'Page background', group: 'page', type: 'color', default: '#142414' },
  { name: '--color-panel-bg', label: 'Panel background', group: 'page', type: 'color', default: '#1a2e1a' },
  { name: '--color-panel-border', label: 'Panel border', group: 'page', type: 'color', default: '#2a4a2a' },
  {
    name: '--color-input-bg',
    label: 'Input background',
    group: 'page',
    type: 'color',
    default: '#2a4a2a',
    derivedFrom: '--color-panel-border',
  },

  // ── Invitation page ──────────────────────────────────────────────────────
  { name: '--color-invitation-bg', label: 'Invitation — background', group: 'invitation', type: 'color', default: '#fbedd7' },
  { name: '--color-envelope-lining', label: 'Envelope — lining', group: 'invitation', type: 'color', default: '#934713' },
  { name: '--color-envelope-top-flap', label: 'Envelope — top flap', group: 'invitation', type: 'color', default: '#934713' },
  { name: '--color-invitation-text', label: 'Invitation — text', group: 'invitation', type: 'color', default: '#142414' },
  {
    name: '--color-envelope-body',
    label: 'Envelope — body',
    group: 'invitation',
    type: 'color',
    default: '#9e4a1c',
    derivedFrom: '--color-accent',
  },

  // ── Typography ───────────────────────────────────────────────────────────
  { name: '--font-display', label: 'Display font', group: 'typography', type: 'font', default: "'Cormorant Garamond', Georgia, serif" },
  { name: '--font-body', label: 'Body font', group: 'typography', type: 'font', default: "'Montserrat', 'Segoe UI', sans-serif" },
  { name: '--font-mono', label: 'Monospace font', group: 'typography', type: 'font', default: "'Courier New', monospace" },

  // ── Layout ───────────────────────────────────────────────────────────────
  { name: '--max-width', label: 'Max content width', group: 'layout', type: 'length', default: '1100px' },
  { name: '--nav-height', label: 'Nav height', group: 'layout', type: 'length', default: '72px' },
] as const;

export const STYLE_VARIABLE_GROUP_LABELS: Record<StyleVariableGroup, string> = {
  accent: 'Primary accent',
  'accent-secondary': 'Secondary accent (blush)',
  highlight: 'Highlight accent (gold)',
  danger: 'Danger',
  status: 'Status pills',
  text: 'Text',
  surface: 'Neutral surfaces',
  page: 'Page chrome',
  invitation: 'Invitation page',
  typography: 'Typography',
  layout: 'Layout',
};

/** Flat map of CSS custom property name -> value. Sparse on the wire — see StyleVariablesService. */
export type StyleVariablesRecord = Record<string, string>;

export interface StyleVariablesResponse {
  variables: StyleVariablesRecord;
  updatedAt: string | null;
}

export interface StyleVariablesPatchRequest {
  /** Variables to upsert — merged into the stored override set. */
  variables: StyleVariablesRecord;
  /** Variable names to clear — removed from the stored override set, reverting to the compiled default. */
  resets: string[];
}

/** Shape written/read by StyleVariablesService.download()/parseFile(). */
export interface StyleVariablesFile {
  variables: StyleVariablesRecord;
  exportedAt: string;
}
